import { PropsWithChildren, useContext, useEffect } from "react"
import { useMatch, useNavigate } from "react-router-dom"
import { Collapse, Grid, List, ListItem, ListItemButton, ListItemText } from "@mui/material"
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome"
import { makeStyles } from "tss-react/mui"
import { LayoutContainer } from "./LayoutContainer"
import { NavMenuContext, NavMenuItem } from "./NavMenuItem"
import { usePreserveDateRangeNavigation } from "../hooks/usePreserveDateRangeNavigation"

type StyleProps = Readonly<{
    compact?: boolean
    isActive: boolean
}>

const useStyles = makeStyles<StyleProps>()(() => ({
    wrapList: {
        marginLeft: "1rem",
        borderLeft: `2px solid rgba(255, 255, 255, 0.5)`,
    },
}))

type NavMenuItemGroupProps = Readonly<
    PropsWithChildren<{
        icon: FontAwesomeIconProps["icon"]
        dest: string
        title: string
        isMobile?: boolean
        dataTourId?: string
    }>
>

export const NavMenuItemGroupWrapper = ({
    icon,
    dest,
    title,
    children,
    isMobile,
    dataTourId,
}: NavMenuItemGroupProps) => {
    return (
        <NavMenuItemGroup icon={icon} dest={dest} title={title} isMobile={isMobile} dataTourId={dataTourId}>
            {children}
        </NavMenuItemGroup>
    )
}

export function NavMenuItemGroup({ icon, dest, children, title, isMobile, dataTourId }: NavMenuItemGroupProps) {
    const { compact, onCompactToggle, openMenuId, setOpenMenuId } = useContext(NavMenuContext)
    const matchesCurrent = useMatch(`${dest}/*`)
    const { classes } = useStyles({ compact, isActive: matchesCurrent !== null })

    const isOpen = isMobile ?? openMenuId === dest

    const handleClickCollapse = () => {
        compact && onCompactToggle()
        setOpenMenuId(isOpen ? null : dest)
    }

    useEffect(() => {
        if (matchesCurrent !== null) {
            setOpenMenuId(dest)
        }
    }, [matchesCurrent, dest, setOpenMenuId])

    useEffect(() => {
        compact && setOpenMenuId(null)
    }, [compact])

    return (
        <>
            <NavMenuItem icon={icon} dest={`${dest}/*`} handleItemClick={handleClickCollapse} dataTourId={dataTourId}>
                {title}
            </NavMenuItem>
            <Collapse in={isOpen} unmountOnExit>
                <List component="div" disablePadding>
                    <ListItem disableGutters>
                        <LayoutContainer>
                            <Grid className={classes.wrapList}>{children}</Grid>
                        </LayoutContainer>
                    </ListItem>
                </List>
            </Collapse>
        </>
    )
}

const useStylesCollapse = makeStyles<StyleProps>()((theme, { isActive }) => ({
    navItemButton: {
        padding: "4px 0",
        paddingLeft: "1.5rem",
    },
    navItemCollapseText: {
        color: theme.palette.tailwind.slate[100],
        transition: "opacity 0.25s",
        margin: 0,
        "& >span": {
            fontWeight: isActive ? 700 : 400,
            fontSize: "0.875rem",
        },
    },
}))

type NavMenuSubItemProps = Readonly<
    PropsWithChildren<{
        route: string
    }>
>

export const NavMenuSubItem = ({ children, route }: NavMenuSubItemProps) => {
    const navigate = useNavigate()
    const matchesCurrent = useMatch(`${route}/*`)
    const { classes } = useStylesCollapse({ isActive: matchesCurrent !== null })

    const { handleClick } = usePreserveDateRangeNavigation(route)

    return (
        <ListItemButton onClick={handleClick} disableGutters className={classes.navItemButton}>
            <ListItemText primaryTypographyProps={{ variant: "caption" }} className={classes.navItemCollapseText}>
                {children}
            </ListItemText>
        </ListItemButton>
    )
}
